import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { AlertTriangle, Brush } from "lucide-react";
import { DateField, Button, Modal, TextareaField } from "@fawkes/ui";

import { Werklijst, WeergaveKeuze, useWeergave, type Kolom, type Rij } from "../components/WerklijstWeergave";
import { PensionLijstFilters, PensionLijstKop, PensionLijstMeta, PensionLijstTegel, zoekIn } from "../components/PensionLijstKop";
import { formatDate, todayIso } from "../i18n/format";
import "./PensionWerklijst.css";

type Reden = "GEEN_FREQUENTIE" | "VOLGENS_SCHEMA" | "ONBEKEND_SCHEMA" | "NIET_VANDAAG" | "GEDAAN";
type Bevinding = "OK" | "BIJZONDERHEID";

interface Frequentie {
  id: number;
  naam: string;
  schema: string;
}

interface Regel {
  dierId: number;
  dierNaam: string | null;
  diersoort: string | null;
  klantId: number | null;
  klantNaam: string | null;
  kennel: string | null;
  vertrek: string | null;
  frequentieId: number | null;
  frequentie: string | null;
  laatsteBeurt: string | null;
  laatsteDoor: string | null;
  laatsteBevinding: Bevinding | null;
  laatsteOpmerking: string | null;
  teDoen: boolean;
  reden: Reden;
}

interface BorstelenData {
  vestigingId: number;
  datum: string;
  telling: { teDoen: number; gedaan: number; metBijzonderheid: number; zonderFrequentie: number };
  frequenties: Frequentie[];
  regels: Regel[];
}

/** De soortfilters. "Overig" vangt alles wat geen hond of kat is, zodat er nooit iets wegvalt. */
type Soort = "ALLE" | "HOND" | "KAT" | "OVERIG";

function soortVan(diersoort: string | null): Exclude<Soort, "ALLE"> {
  const s = (diersoort ?? "").toLowerCase();
  if (s.startsWith("hond")) return "HOND";
  if (s.startsWith("kat") || s.startsWith("poes")) return "KAT";
  return "OVERIG";
}

/**
 * Het overzicht "Borstelen" — wens §8 uit de wensenlijst.
 *
 * <p>Anders dan Wegen en Bodycheck loopt dit niet op een instelling van de vestiging maar op de
 * frequentie die de klant bij de aanvraag opgaf: elke dag, om de dag, alleen de laatste dag. Die
 * frequentie staat op de reserveringsregel.
 *
 * <p><b>Waarom de gasten zonder frequentie er óók op staan:</b> die frequentie is in de praktijk
 * vrijwel nergens ingevuld (drie van de 137.562 reserveringsregels). Een lijst die daardoor altijd
 * leeg is helpt niemand. Hier staat dus iedereen die vandaag ligt, met zijn frequentie als
 * keuzelijst in de rij — zo raakt hij gevuld terwijl je toch al langsloopt.
 */
export function PensionBorstelen({ onOpenDier }: { onOpenDier?: (dierId: number) => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [datum, setDatum] = useState(() => todayIso());
  const [soort, setSoort] = useState<Soort>("ALLE");
  const [zoek, setZoek] = useState("");
  const [weergave, setWeergave] = useWeergave("borstelen");
  const [initialen, setInitialen] = useState("");
  const [bijzonderheidVoor, setBijzonderheidVoor] = useState<Regel | null>(null);
  const [fout, setFout] = useState<number | null>(null);

  const query = useMemo(() => ({ datum }), [datum]);
  const { data, isLoading, isError, refetch } = useCustom<BorstelenData>({
    url: "pension/borstelen",
    method: "get",
    config: { query },
  });
  const { mutate, isLoading: bezig } = useCustomMutation();

  const borstelen = data?.data;
  const alle = borstelen?.regels ?? [];
  const regels = zoekIn(soort === "ALLE" ? alle : alle.filter((r) => soortVan(r.diersoort) === soort), zoek);

  const aantalPerSoort = (s: Soort) =>
    s === "ALLE" ? alle.length : alle.filter((r) => soortVan(r.diersoort) === s).length;

  function afvinken(regel: Regel, bevinding: Bevinding, opmerking?: string) {
    setFout(null);
    mutate(
      {
        url: "pension/borstelen",
        method: "post",
        values: { dierId: regel.dierId, initialen: initialen.trim() || null, bevinding, opmerking },
      },
      {
        onSuccess: () => {
          setBijzonderheidVoor(null);
          refetch();
        },
        onError: () => setFout(regel.dierId),
      },
    );
  }

  function zetFrequentie(regel: Regel, waarde: string) {
    setFout(null);
    mutate(
      {
        url: "pension/borstelen/frequentie",
        method: "put",
        values: { dierId: regel.dierId, borstelSoortId: waarde === "" ? null : Number(waarde) },
      },
      { onSuccess: () => refetch(), onError: () => setFout(regel.dierId) },
    );
  }

  // Tabel en tegels komen uit dezelfde kolommen (WerklijstWeergave).
  const kolommen: Kolom<(typeof regels)[number]>[] = [
    {
      kop: t("pensionBorstelen.kolom.dier"),
      cel: (r) => (
        <>
          {onOpenDier ? (
            <button
              type="button"
              className="pension-werklijst__link"
              onClick={() => onOpenDier(r.dierId)}
            >
              {r.dierNaam ?? "—"}
            </button>
          ) : (
            (r.dierNaam ?? "—")
          )}
          {r.diersoort && <span className="pension-chip">{r.diersoort}</span>}
        </>
      ),
      rol: "dier",
    },
    {
      kop: t("pensionBorstelen.kolom.klant"),
      cel: (r) => <>{r.klantNaam ?? "—"}</>,
      rol: "klant",
    },
    {
      kop: t("pensionBorstelen.kolom.kennel"),
      cel: (r) => <>{r.kennel ?? "—"}</>,
      rol: "verblijf",
    },
    {
      kop: t("pensionBorstelen.kolom.frequentie"),
      cel: (r) => (
        <>
          <select
            className="pension-werklijst__keuze"
            value={r.frequentieId ?? ""}
            aria-label={t("pensionBorstelen.frequentieVoor", { args: [r.dierNaam ?? ""] })}
            onChange={(e) => zetFrequentie(r, e.target.value)}
          >
            <option value="">{t("pensionBorstelen.geenFrequentie")}</option>
            {(borstelen?.frequenties ?? []).map((f) => (
              <option key={f.id} value={f.id}>
                {f.naam}
              </option>
            ))}
          </select>
        </>
      ),
    },
    {
      kop: t("pensionBorstelen.kolom.laatst"),
      cel: (r) => (
        <>
          {r.laatsteBeurt ? formatDate(r.laatsteBeurt, locale) : "—"}
          {r.laatsteDoor && <span className="pension-werklijst__door"> · {r.laatsteDoor}</span>}
          {r.laatsteBevinding === "BIJZONDERHEID" && r.laatsteOpmerking && (
            <span className="pension-werklijst__verschil" data-signaal="ACTIE_NODIG">
              <AlertTriangle size={13} aria-hidden="true" />
              {r.laatsteOpmerking}
            </span>
          )}
        </>
      ),
    },
    {
      kop: t("pensionBorstelen.kolom.reden"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__reden" data-reden={r.reden}>
            {t(`pensionBorstelen.reden.${r.reden}`)}
          </span>
        </>
      ),
    },
    {
      kop: t("pensionBorstelen.kolom.afvinken"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__invoer">
            <Button variant="primary" disabled={bezig} onClick={() => afvinken(r, "OK")}>
              {t("pensionBorstelen.gedaan")}
            </Button>
            <Button variant="secondary" onClick={() => setBijzonderheidVoor(r)}>
              {t("pensionBorstelen.bijzonderheid")}
            </Button>
            {fout === r.dierId && (
              <span className="fawkes-error" role="status">
                {t("pensionBorstelen.mislukt")}
              </span>
            )}
          </span>
        </>
      ),
      rol: "actie",
    },
  ];
  const rij = (r: (typeof regels)[number]): Rij => ({ key: r.dierId, "data-signaal": r.laatsteBevinding === "BIJZONDERHEID" ? "ACTIE_NODIG" : "OK", "data-tewegen": r.teDoen ? "" : undefined });

  return (
    <div className="pension-werklijst fawkes-pgo">
      <PensionLijstKop icoon={<Brush size={22} />} titel={t("pensionBorstelen.title")} subtitel={t("pensionBorstelen.subtitle")} soort={{ opties: (["HOND", "KAT", "OVERIG"] as Soort[]).map((s) => ({ sleutel: s, label: t(`pensionBorstelen.soort.${s}`), aantal: aantalPerSoort(s) })), actief: soort, onKies: (s) => setSoort(s as Soort), totaal: aantalPerSoort("ALLE") }}>
          <PensionLijstTegel waarde={borstelen?.telling.teDoen} label={t("pensionBorstelen.teller.teDoen")} />
          <PensionLijstTegel waarde={borstelen?.telling.gedaan} label={t("pensionBorstelen.teller.gedaan")} />
          <PensionLijstTegel
            waarde={borstelen?.telling.metBijzonderheid}
            label={t("pensionBorstelen.teller.bijzonderheid")}
            toon={borstelen && borstelen.telling.metBijzonderheid > 0 ? "rood" : undefined}
          />
          <PensionLijstTegel
            waarde={borstelen?.telling.zonderFrequentie}
            label={t("pensionBorstelen.teller.zonderFrequentie")}
          />
      </PensionLijstKop>

      <PensionLijstFilters
        zoek={zoek}
        onZoek={setZoek}
        weergave={<WeergaveKeuze waarde={weergave} onKies={setWeergave} />}
        rechts={
          <div className="pension-werklijst__balk">
            <input
              type="text"
              className="pension-werklijst__initialen"
              value={initialen}
              maxLength={10}
              placeholder={t("pensionBorstelen.initialen")}
              aria-label={t("pensionBorstelen.initialen")}
              onChange={(e) => setInitialen(e.target.value)}
            />
            <label className="po-veld werklijst-datum">
              <span>{t("pensionOverzichten.datum")}</span>
              <DateField className="pension-werklijst__datum"
                value={datum}
                onChange={(e) => e.target.value && setDatum(e.target.value)}
                aria-label={t("pensionBorstelen.datum")}
              />
            </label>
            <Button variant="secondary" onClick={() => setDatum(todayIso())}>
              {t("pensionBorstelen.vandaag")}
            </Button>
          </div>
        }
      />

      {isError ? (
        <p className="fawkes-muted">{t("pensionBorstelen.loadFailed")}</p>
      ) : (
        <>
          <PensionLijstMeta aantal={regels.length} />
          {isLoading ? (
            <p className="pension-werklijst__leeg">{t("common.loading")}</p>
          ) : regels.length === 0 ? (
            <p className="pension-werklijst__leeg">
              <Brush size={26} aria-hidden="true" />
              {t("pensionBorstelen.leeg")}
            </p>
          ) : (
            <Werklijst lijst="borstelen" regels={regels} kolommen={kolommen} weergave={weergave} rij={rij} dierId={(r) => r.dierId} />
          )}
        </>
      )}

      {bijzonderheidVoor && (
        <BijzonderheidDialoog
          regel={bijzonderheidVoor}
          bezig={bezig}
          onClose={() => setBijzonderheidVoor(null)}
          onOpslaan={(opmerking) => afvinken(bijzonderheidVoor, "BIJZONDERHEID", opmerking)}
        />
      )}
    </div>
  );
}


/** Wat er gezien is. Zonder tekst heeft "iets gezien" geen betekenis, dus opslaan kan niet leeg. */
function BijzonderheidDialoog({
  regel,
  bezig,
  onClose,
  onOpslaan,
}: {
  regel: Regel;
  bezig: boolean;
  onClose: () => void;
  onOpslaan: (opmerking: string) => void;
}) {
  const t = useTranslate();
  const [opmerking, setOpmerking] = useState("");

  return (
    <Modal
      open
      title={t("pensionBorstelen.bijzonderheid.titel", { args: [regel.dierNaam ?? ""] })}
      onClose={onClose}
    >
      <TextareaField
        label={t("pensionBorstelen.bijzonderheid.wat")}
        value={opmerking}
        rows={4}
        onChange={(e) => setOpmerking(e.target.value)}
      />
      <div className="pension-werklijst__dialoogknoppen">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={bezig || opmerking.trim().length === 0}
          onClick={() => onOpslaan(opmerking.trim())}
        >
          {t("common.save")}
        </Button>
      </div>
    </Modal>
  );
}
